import app from 'flarum/forum/app';

// ===== Push notification permission reporting =====
// The Notification.permission state only exists in the browser. We cannot query
// it from the server, so each visit reports the current state back so admins can
// see who actually authorized notifications on their device.

const STORAGE_KEY = 'florcarioca-push-permission';

// The cache is per-user: several accounts may share the same browser, and each
// of them must send its own report (the server stores the state per user).
export const storageKeyForUser = () => STORAGE_KEY + '-' + app.session.user.id();

export const pushConfigured = () => !!app.forum.attribute('vapidPublicKey');

export const getBrowserPermission = () => ('Notification' in window ? window.Notification.permission : 'unsupported');

// The Notification.permission getter can be unreliable (Firefox does not update
// it after the prompt) and shims introduced by extensions can skew the value
// resolved by requestPermission(). The browser's own permissions API reflects
// the true origin permission, so prefer it whenever available.
export const getTruePermission = async () => {
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const status = await navigator.permissions.query({ name: 'notifications' });
      if (status.state === 'granted' || status.state === 'denied') {
        return status.state;
      }
    }
  } catch (e) {
    // query() not supported for notifications: fall through to the getter
  }
  return getBrowserPermission();
};

// Report the permission to the server. `explicitPermission` is used right
// after the user answers a prompt; otherwise the true origin permission is
// read (permissions API first, Notification getter as fallback).
export const reportPushPermission = (force = false, explicitPermission = null) => {
  // Guests don't have a profile to report to. Note: app.session is only
  // available after the app has finished booting.
  if (!app.session || !app.session.user) return;

  const readPermission = async () =>
    explicitPermission || (await getTruePermission());

  return readPermission().then((permission) => {
    if (['granted', 'denied', 'default', 'unsupported'].indexOf(permission) === -1) {
      return;
    }

    // Avoid spamming the API with identical reports (per device/browser)
    if (!force) {
      let lastReported = null;
      try {
        lastReported = window.localStorage.getItem(storageKeyForUser());
      } catch (e) {
        // localStorage unavailable (e.g. private browsing) -> always report
      }
      if (lastReported === permission) return;
    }

    return app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/push-permission',
        body: {
          data: {
            type: 'users',
            id: app.session.user.id(),
            attributes: { permission },
          },
        },
      })
      .then(() => {
        try {
          window.localStorage.setItem(storageKeyForUser(), permission);
        } catch (e) {
          // ignore storage errors
        }
        // Update the in-memory model so UI badges refresh immediately
        app.session.user.pushAttributes({
          pushPermission: permission,
          pushPermissionAt: Math.floor(Date.now() / 1000),
        });
        m.redraw();
      })
      .catch(() => {
        // Network error: retry on next visit / tab focus
      });
  });
};

// Safety net: the event-driven hooks (boot, visibilitychange, permission.onchange,
// requestPermission wrapper) can miss an activation that happens outside this
// page — e.g. the user toggles notifications in the browser's own settings, or
// fof/pwa subscribes through its own code path. Polling the permission state
// and reporting whenever it differs from what we last sent guarantees the admin
// panel always reflects the true state, without any manual "Refresh" click.
// POSTing is cheap and deduped via localStorage: at most one request per change.
const startPushPermissionPolling = () => {
  setInterval(() => {
    if (document.visibilityState === 'visible') {
      reportPushPermission();
    }
  }, 10000);
};

export const initPushPermissionReporting = () => {
  // app.session is not ready when the initializer runs, so defer the first
  // report until the app has finished booting. This first report is FORCED:
  // a page load is the authoritative moment to sync the current permission to
  // the server, so a stale localStorage dedup value must not swallow it.
  setTimeout(() => reportPushPermission(true), 0);

  startPushPermissionPolling();

  // Re-report whenever the user returns to the tab (permission may have been
  // revoked/re-enabled in browser settings or another tab).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      reportPushPermission();
    }
  });

  // React to permission changes as they happen (Chrome/Firefox).
  try {
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: 'notifications' })
        .then((status) => {
          status.onchange = () => reportPushPermission();
        })
        .catch(() => {});
    }
  } catch (e) {
    // Browser does not support querying notification permission: the
    // visibilitychange listener above will pick up changes.
  }

  // Report right after the user answers the browser's permission prompt
  // (e.g. clicking the opt-in button on the settings page). We wrap
  // Notification.requestPermission so both the callback and promise APIs
  // re-report once the user has decided. The native API is never invoked
  // twice per call (which would stack permission prompts on old browsers).
  if ('Notification' in window && window.Notification.requestPermission) {
    const original = window.Notification.requestPermission;

    window.Notification.requestPermission = function (callback) {
      const hasCallback = typeof callback === 'function';

      const reReport = (status) => {
        setTimeout(() => reportPushPermission(), 0);
        if (hasCallback) {
          callback(status);
        }
        return status;
      };

      // Legacy browsers only support the callback API: forward ours directly.
      if (hasCallback) {
        return original.call(window.Notification, reReport);
      }

      const result = original.call(window.Notification);

      if (result && typeof result.then === 'function') {
        return result.then(reReport);
      }

      // Legacy API without a callback: nothing to forward, nothing to report
      // here (the visibilitychange listener picks up changes later).
      return result;
    };
  }
};